import { potionArt } from '../../utils/arena/alchemist';
import { POTION_LIQUID } from './potionLiquid';
import { useLayoutEffect, useRef } from 'preact/hooks';
import PotionGrainMask from './PotionGrainMask';

// Emission belongs to the separated liquid material. The saturated body stays
// darker at its edges; moving light in its depth lights the meniscus and casts
// bloom OUTSIDE that shape onto the glass and fittings.
export default function PotionBrewGlow({ potion, materialId, brew }) {
  const id = `${materialId}-brew`;
  const liquid = POTION_LIQUID[potion];
  const root = useRef(null);
  const envelope = useRef(null);

  useLayoutEffect(() => {
    const element = root.current;
    if (!element?.animate) return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const illuminate = () => {
      // Sample before cancelling: a second craft continues from the visible
      // brightness rather than remounting or jumping back to the start.
      const current = Number(getComputedStyle(element).opacity) || 0;
      envelope.current?.cancel();
      const peak = media.matches ? 0.28 : Math.min(0.98, Math.max(0.74, current + 0.16));
      envelope.current = element.animate([
        { opacity: current, offset: 0, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' },
        { opacity: peak, offset: 0.12, easing: 'linear' },
        { opacity: peak * 0.95, offset: 0.36, easing: 'cubic-bezier(0.77, 0, 0.175, 1)' },
        { opacity: 0, offset: 1 },
      ], { duration: media.matches ? 1100 : 2800, fill: 'forwards' });
    };
    illuminate();
    media.addEventListener('change', illuminate);
    return () => media.removeEventListener('change', illuminate);
  }, [brew.id]);

  useLayoutEffect(() => () => envelope.current?.cancel(), []);
  return (
    <g ref={root} data-testid="potion-brew-glow" data-potion={potion} className="vh-brew-liquid">
      <defs>
        <PotionGrainMask id={`${id}-wax`} potion={potion} />
        <radialGradient id={`${id}-body`} gradientUnits="userSpaceOnUse" cx={liquid.cx} cy={liquid.cy} r="315">
          <stop stopColor={liquid.color} />
          <stop offset=".4" stopColor={liquid.color} />
          <stop offset=".82" stopColor={liquid.deep} />
          <stop offset="1" stopColor={liquid.deep} />
        </radialGradient>
        <radialGradient id={`${id}-core`}>
          <stop stopColor={liquid.hot} stopOpacity=".7" />
          <stop offset=".25" stopColor={liquid.hot} stopOpacity=".4" />
          <stop offset=".65" stopColor={liquid.color} stopOpacity=".18" />
          <stop offset="1" stopColor={liquid.color} stopOpacity="0" />
        </radialGradient>
        <filter id={`${id}-flow`} x="-40%" y="-40%" width="180%" height="180%" colorInterpolationFilters="sRGB">
          <feTurbulence type="fractalNoise" baseFrequency=".009 .017" numOctaves="2" seed="8" result="eddies" />
          <feDisplacementMap in="SourceGraphic" in2="eddies" scale="65" xChannelSelector="R" yChannelSelector="G" />
          <feGaussianBlur stdDeviation="12" />
        </filter>
        <filter id={`${id}-spill`} x="-50%" y="-50%" width="200%" height="200%" colorInterpolationFilters="sRGB">
          <feGaussianBlur stdDeviation="14" result="light" />
          <feTurbulence type="fractalNoise" baseFrequency=".025 .08" numOctaves="2" seed="17" result="tooth" />
          <feDisplacementMap in="light" in2="tooth" scale="24" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <filter id={`${id}-bloom`} x="-60%" y="-60%" width="220%" height="220%" colorInterpolationFilters="sRGB">
          <feGaussianBlur stdDeviation="32" result="light" />
          <feTurbulence type="fractalNoise" baseFrequency=".015 .04" numOctaves="2" seed="31" result="tooth" />
          <feDisplacementMap in="light" in2="tooth" scale="40" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        {/* Keep the existing crayon marks and white glass reflections above the
            emitting body, instead of brightening another copy of the bottle. */}
        <filter id={`${id}-reflections`} colorInterpolationFilters="sRGB">
          <feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  .2126 .7152 .0722 0 0" />
          <feComponentTransfer><feFuncA type="table" tableValues="0 0 0 0 .15 .55" /></feComponentTransfer>
        </filter>
      </defs>
      <g className="vh-brew-spill" mask={`url(#${id}-wax)`} style={{ mixBlendMode: 'screen' }}>
        <use href={`#${materialId}-volume`} fill={liquid.color} filter={`url(#${id}-bloom)`} opacity=".5" />
        <use href={`#${materialId}-volume`} fill={liquid.color} filter={`url(#${id}-spill)`} opacity=".8" />
      </g>
      <g clipPath={`url(#${materialId}-liquid)`}>
        <g className="vh-brew-emission" mask={`url(#${id}-wax)`} style={{ isolation: 'isolate' }}>
          <rect width="1024" height="1024" fill={`url(#${id}-body)`} />
          <g filter={`url(#${id}-flow)`}>
            <ellipse className="vh-brew-core" cx={liquid.cx - 25} cy={liquid.cy + 10} rx="235" ry="260" fill={`url(#${id}-core)`} />
            <ellipse className="vh-brew-eddy" cx={liquid.cx + 100} cy={liquid.cy + 85} rx="210" ry="175" opacity=".35" fill={`url(#${id}-core)`} />
          </g>
          <image href={potionArt(potion)} width="1024" height="1024" style={{ mixBlendMode: 'soft-light', opacity: .9 }} />
        </g>
        <image className="vh-brew-glass" href={potionArt(potion)} width="1024" height="1024" filter={`url(#${id}-reflections)`} />

      </g>
    </g>
  );
}
